import { DateUtil, DisplayType, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutDriverEvent } from "./autogen/AutogenLayoutDriverEvent";
import { BlurEvent, ChangeEvent, Component, DataDisplayEvent, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownSelectionEvent, ForcedCase, LookupModelSelectionEvent } from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { TextboxValidator } from "@mcleod/components/src/components/textbox/TextboxValidator";
import { getDispatchControlBoolean } from "@mcleod/general";

export class DriverEvent extends AutogenLayoutDriverEvent {
    notificationDays: number = 0;
    autoCloseDays: number = 0;

    onLoad(): void | Promise<void> {
        this.setupLocationComponents();
        this.citystateEventLocation._longtiudeField = "longitude";
        this.citystateEventLocation._latitudeField = "latitude";
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            return this.inactivateAutoHomeLocationOnStatusChange();
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxEquipmentId. */
    textboxEquipmentIdBeforeLookupModelSearch(event) {
        event.filter.is_active = "Y";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDriverEvent. */
    textboxDriverEventBeforeLookupModelSearch(event) {
        event.filter.equip_type = "D";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDriverManager. */
    textboxDriverManagerBeforeLookupModelSearch(event) {
        event.filter.web_user_type = 'U';
    }

    /** This is an event handler for the onLookupModelSelection event of textboxDriverEvent. */
    textboxDriverEventOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.textboxNotifyStartDate.clear();
        this.textboxNotifyEndDate.clear();
        if (event.hasSelection()) {
            const selection = event.selection;
            this.notificationDays = selection.get("msg_warning_days");
            this.autoCloseDays = selection.get("auto_close_days");

            if (TextboxValidator.validate(this.textboxEventFromDate, true, false).isValid) {
                const eventFromDate = this.textboxEventFromDate.text;
                const notifyStartDate = this.calculateNotificationDate(this.textboxDriverEvent.text, eventFromDate,
                    (-1) * this.notificationDays, this.formatDateOnly(new Date()));
                this.textboxNotifyStartDate.text = notifyStartDate;
                this.mainDataSource?.activeRow.set("notify_start_date", DateUtil.parseDateByDisplayType(this.textboxNotifyStartDate.text, DisplayType.DATE));
            }

            if (TextboxValidator.validate(this.textboxEventToDate, true, false).isValid) {
                const eventToDate = this.textboxEventToDate.text;
                const notifyEndDate = this.calculateNotificationDate(this.textboxDriverEvent.text, eventToDate,
                    this.autoCloseDays, eventToDate);
                this.textboxNotifyEndDate.text = notifyEndDate;
                this.mainDataSource?.activeRow.set("notify_end_date", DateUtil.parseDateByDisplayType(this.textboxNotifyEndDate.text, DisplayType.DATE));
            }

            const defaultPriority = event.getValueFromSelectedRow("priority");
            this.mainDataSource.activeRow.set("priority", defaultPriority);
        }
    }

    switchLocationTypeOnChange(event: ChangeEvent) {
        // Toggle visibility based on location type
        const usingCityState = event.newValue;
        this.locationEventLocation.visible = this.locationEventLocation.required = !usingCityState;
        this.citystateEventLocation.visible = this.citystateEventLocation.required = usingCityState;

        // Ensure components are bound to data source
        if (!this.mainDataSource.isBoundComponent(this.locationEventLocation)) {
            this.mainDataSource.addBoundComponent(this.locationEventLocation);
        }
        if (!this.mainDataSource.isBoundComponent(this.citystateEventLocation)) {
            this.mainDataSource.addBoundComponent(this.citystateEventLocation);
        }
        
        try {
            // Clear location data in one centralized call
            this.clearLocationData(event.userInitiatedChange, true, true);
            
            if (usingCityState) {
                // When switching to CityState mode
                this.resetComponent(this.locationEventLocation);
                this.sourceAutoHomeLocation.removeBoundComponent(this.labelLocationAutoHome);
            } else {
                // When switching to Location mode
                this.resetComponent(this.citystateEventLocation);
            }
        } catch (e) {
            console.error("Error in switchLocationTypeOnChange:", e);
        }
    }

    /**
     * Centralized method to clear location data
     * @param clearMainSource Whether to clear the main data source
     * @param clearAutoHome Whether to clear the auto home data source
     * @param clearComponents Whether to clear the UI components
     */
    clearLocationData(clearMainSource = true, clearAutoHome = true, clearComponents = true) {
        // Define location-related fields for more maintainable code
        const locationFields = [
            "location_id", "city", "city_name", "city_id", "state", 
            "zip_code", "location_name", "address", "latitude", "longitude"
        ];
        
        // Clear main data source fields
        if (clearMainSource && this.mainDataSource.activeRow) {
            locationFields.forEach(field => {
                this.mainDataSource.activeRow.set(field, null);
            });
        }
        
        // Clear auto home location fields
        if (clearAutoHome && this.sourceAutoHomeLocation?.activeRow) {
            locationFields.forEach(field => {
                this.sourceAutoHomeLocation.activeRow.set(field, null);
            });
            this.labelLocationAutoHome?.clearAllFields();
            this.resetCityStateComponent(this.citystateCityAutoHome);
        }
        
        // Clear UI components
        if (clearComponents) {
            // Clear Location components
            this.locationEventLocation?.clearAllFields();
            this.labelLocationAutoHome?.clearAllFields();
            
            // Clear CityState components
            this.citystateEventLocation?.clearAllFields();
            this.resetCityStateComponent(this.citystateCityAutoHome);
        }
    }
    
    /**
     * Legacy method maintained for backward compatibility
     */
    clearLocationFields() {
        this.clearLocationData(true, true, true);
    }
    
    resetComponent(comp: Component) {
        this.mainDataSource.removeBoundComponent(comp);
    }
    
    /**
     * Generic method to reset any CityState component completely
     * @param component The CityState component to reset
     */
    private resetCityStateComponent(component) {
        if (!component) return;
        
        // First clear all fields using the component's method
        component.clearAllFields();
        
        // Get all textboxes from the component
        const textboxes = [
            component.textCombined,
            component.textCity,
            component.textState,
            component.textZip
        ];
        
        // Thoroughly clear each textbox
        textboxes.forEach(textbox => {
            if (textbox) {
                textbox.text = "";
                textbox.clear();
                textbox.resetValidation();
            }
        });
        
        // Force a refresh of the component if we have data
        if (this.sourceAutoHomeLocation?.activeRow) {
            component.displayComponentData(this.sourceAutoHomeLocation.activeRow, null, 0);
        }
    }
    
    setupLocationComponents() {
        this.citystateEventLocation.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateEventLocation.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateEventLocation.textCombined.addChangeListener((event: ChangeEvent) => {
            if (event.newValue == "")
                this.locationEventLocation.textCombined.clear();
        });
        this.locationEventLocation.textCombined.addChangeListener((event: ChangeEvent) => {
            if (event.newValue == "")
                this.citystateEventLocation.textCombined.clear();
        });

        this.setupAutoHomeLocationListeners();
    }

    /**
     * Sets up change listeners to automatically update auto home fields
     * when location fields change (if switchCreateAutoHome is checked)
     */
    private setupAutoHomeLocationListeners() {
        this.locationEventLocation.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (this.switchCreateAutoHome.checked && !this.switchLocationType.checked) {
                if (!event.hasSelection()) {
                    this.clearLocationData(event.userInitiatedChange, true, false);
                }
                this.copyLocationToAutoHome();
            }
        });
        this.citystateEventLocation.textCombined.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (this.switchCreateAutoHome.checked && this.switchLocationType.checked) {
                if (!event.hasSelection()) {                    
                    this.clearLocationData(event.userInitiatedChange, true, false);
                    this.resetCityStateComponent(this.citystateCityAutoHome);
                }
                this.copyCityStateToAutoHome();
            }
        });
    }

    addDays(date: Date, days: number): Date {
        date.setDate(date.getDate() + days);
        return date;
    }

    calculateNotificationDate(eventCode: string, inputDate: string, days: number, defaultDate: string):string {
        if ((!eventCode) || (!inputDate)) {return ""}
        if (!days) {return defaultDate}

        const eventDate = DateUtil.parseDateTime(inputDate)
        const notifyDate = this.addDays(eventDate, days);
        return this.formatDateOnly(notifyDate);
    }

    /** This is an event handler for the onChange event of textboxDriverEvent. */
    textboxDriverEventOnChange(event) {
        this.textboxNotifyStartDate.clear();
    }

    formatDateOnly(inputDate: Date): string {
        return inputDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    }

    /** This is an event handler for the beforeExecution event of sourceDriverEvents. */
    sourceDriverEventsBeforeExecution(event: DataSourceExecutionEvent) {
        if (this.sourceDriverEvents.mode == DataSourceMode.ADD) {
            if (!event.dataSource?.activeRow?.get("priority")) {
                event.dataSource?.activeRow.set("priority", 1);
            }
            event.dataSource.activeRow.set("equipment_type_id", "D");
        
            if (!this.switchCreateAutoHome.checked) {
                event.dataSource?.activeRow?.clearLinkedModels();
            }
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxEquipmentId. */
    textboxEquipmentIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.textboxDriverManager.selectedItem = null;
        const fleetManager = event.selection?.get("fleet_manager");
        if (fleetManager) {
            const selectedRowLmData = event.selection.getFirstLookupModelData("fleet_manager");

            const name = selectedRowLmData.get("name");
            const id = selectedRowLmData.get("id");
            const lmData = new ModelRow("common/users", false, { name, id});

            const fleetManager = event.getValueFromSelectedRow("fleet_manager");
            this.activeRow.setValueAndLookupModelData("driver_mgr_id", fleetManager, lmData);
        }
    }

    /** This is an event handler for the afterExecution event of sourceDriverEvents. */
    async sourceDriverEventsAfterExecution(event: DataSourceExecutionEvent) {
        this.notificationDays = this.mainDataSource.activeRow?.get("equipstatuscode.msg_warning_days");

        if (this.switchCreateAutoHome?.checked
            && this.sourceAutoHomeLocation?.activeRow
            && this.sourceAutoHomeLocation.mode === DataSourceMode.ADD) {
            const mainId = event.dataSource.activeRow.get("id");
            const driverId = event.dataSource.activeRow.get("equipment_id");
            if (mainId) {
                this.sourceAutoHomeLocation.activeRow.set("equipstatus_id", mainId);
            }
            if (driverId) {
                this.sourceAutoHomeLocation.activeRow.set("driver_id", driverId);
            }
            await this.postAutoHomeLocation().catch(err => {
               CommonDialogs.showError(err);
            });
        }
        if (event.dataSource.mode !== DataSourceMode.ADD && !event.dataSource.activeRow?.isNull("id")) {
           this.sourceAutoHomeLocation.parentDataSource = this.mainDataSource;
           await this.sourceAutoHomeLocation.search({"equipstatus_id": event.dataSource.activeRow.get("id")});
        }

    }

    private async postAutoHomeLocation(): Promise<void> {
        try {
            await this.sourceAutoHomeLocation.activeRow.post();
        } catch (err) {
            CommonDialogs.showError(err);
        }
    }

    sourceDriverEventsOnDisplay(event: DataDisplayEvent) {
        if (this.mainDataSource.mode !== DataSourceMode.ADD && this.activeRow?.isNull("location_id")) {
            this.switchLocationType.checked = true;
        }
    }

    /** This is an event handler for the onBlur event of textboxEventFromDate. */
    textboxEventFromDateOnBlur(event: BlurEvent) {
        if (this.sourceDriverEvents.mode == DataSourceMode.NONE) return;
        this.textboxNotifyStartDate.clear();
        const eventFromDate = this.textboxEventFromDate.text;
        if(!StringUtil.isEmptyString(eventFromDate)) {
            const notifyFromDate = this.calculateNotificationDate(this.textboxDriverEvent.text, eventFromDate,
                (-1) * this.notificationDays, this.formatDateOnly(new Date()));
            if(notifyFromDate !== DateUtil.INVALID_DATE_STRING) {
                this.textboxNotifyStartDate.text = notifyFromDate;
            }
        }
        if (TextboxValidator.validate(this.textboxEventFromDate, true, false).isValid) {
            this.mainDataSource?.activeRow.set("notify_start_date", DateUtil.parseDateByDisplayType(this.textboxNotifyStartDate.text, DisplayType.DATE));
        }
    }

    /** This is an event handler for the onBlur event of textboxEventToDate. */
    textboxEventToDateOnBlur(event: BlurEvent) {
        if (this.sourceDriverEvents.mode == DataSourceMode.NONE) return;
        this.textboxNotifyEndDate.clear();
        const eventToDate = this.textboxEventToDate.text;
        if(!StringUtil.isEmptyString(eventToDate)) {
            const notifyToDate = this.calculateNotificationDate(this.textboxDriverEvent.text, eventToDate,
            this.autoCloseDays, this.formatDateOnly(DateUtil.parseDateTime(eventToDate)));
            if (notifyToDate !== DateUtil.INVALID_DATE_STRING) {
                this.textboxNotifyEndDate.text = notifyToDate;
            }
        }
        if (TextboxValidator.validate(this.textboxEventToDate, true, false).isValid) {
            this.mainDataSource?.activeRow.set("notify_end_date", DateUtil.parseDateByDisplayType(this.textboxNotifyEndDate.text, DisplayType.DATE));
        }
    }

    /** This is an event handler for the onChange event of switchCreateAutoHome. */
    async switchCreateAutoHomeOnChange(event: ChangeEvent) {
        this.panelCreateAutoHomeFields.visible = event.newValue;
        
        if (event.newValue) {
            // Ensure the auto home data source is initialized
            if (!this.sourceAutoHomeLocation.activeRow) {
                this.sourceAutoHomeLocation.setRowsAndMode(DataSourceMode.ADD, [await this.sourceAutoHomeLocation.createBlankRow()]);
                this.sourceAutoHomeLocation.activeRow.set("type_of", "E");
            }
            else {
                if (this.sourceAutoHomeLocation.activeRow.isNull("type_of")) {
                    this.sourceAutoHomeLocation.activeRow.set("type_of", "E");
                }
            }        
            this.copyEventLocationToAutoHome();
        }
    }

    /**
     * Copies location values from the event location to auto home fields
     * based on the current location type (location vs city/state)
     */
    private copyEventLocationToAutoHome() {
        const isUsingCityState = this.switchLocationType.checked;        
        if (isUsingCityState) {
            this.copyCityStateToAutoHome();
        } else {
            this.copyLocationToAutoHome();
        }
    }

    /**
     * Helper function to copy basic location fields from source to target row
     */
    private copyBasicLocationFields(sourceRow: ModelRow, targetRow: ModelRow) {
        const basicFields = ["city_name", "state", "zip_code", "city_id"];
        basicFields.forEach(field => {
            const value = sourceRow.get(field === "city_name" ? "city" : field);
            if (value !== null && value !== undefined) {
                targetRow.set(field, value);
            }
        });
    }

    /**
     * Helper function to set coordinate data from lookup model data
     */
    private setCoordinatesFromLookupData(targetRow: ModelRow, lookupData: any) {
        if (!lookupData) return;
        
        const latitude = lookupData.get("latitude");
        const longitude = lookupData.get("longitude");
        
        if (latitude != null && latitude !== undefined) {
            targetRow.set("latitude", latitude);
        }
        if (longitude != null && longitude !== undefined) {
            targetRow.set("longitude", longitude);
        }
    }

    /**
     * Copies values from locationEventLocation to labelLocationAutoHome
     */
    private async copyLocationToAutoHome() {        
        const sourceRow = this.mainDataSource.activeRow;
        if (!sourceRow) return;

        const targetRow = this.sourceAutoHomeLocation.activeRow;

        // Copy location ID if available
        const locationId = sourceRow.get("location_id");
        if (locationId) {
            targetRow.set("location_id", locationId);
            targetRow.setValueAndLookupModelData("location_id", locationId, null);
        }
        
        // Copy basic location fields using helper
        this.copyBasicLocationFields(sourceRow, targetRow);
        
        // Get lookup data for additional fields
        const lookupData = this.locationEventLocation.textCombined.lookupModelData;
        if (lookupData && lookupData[0]) {
            const firstLookupRow = lookupData[0];
            
            // Set additional location fields
            if (firstLookupRow.get("name")) {
                targetRow.set("location_name", firstLookupRow.get("name"));
            }
            if (firstLookupRow.get("address1")) {
                targetRow.set("address", firstLookupRow.get("address1"));
            }
            
            // Set coordinates using helper
            this.setCoordinatesFromLookupData(targetRow, firstLookupRow);
            
            this.labelLocationAutoHome.displayData(targetRow, null, 0);
        }
    }

    /**
     * Copies values from citystateEventLocation to citystateCityAutoHome
     */
    private copyCityStateToAutoHome() {
        const sourceRow = this.mainDataSource.activeRow;
        if (!sourceRow) return;

        const targetRow = this.sourceAutoHomeLocation.activeRow;
        
        // Copy basic location fields using helper
        this.copyBasicLocationFields(sourceRow, targetRow);
        
        // Set city lookup data
        const cityId = sourceRow.get("city_id");
        if (cityId) {
            targetRow.setValueAndLookupModelData("city_id", cityId, null);
        }
        
        // Get coordinates from city lookup data
        const cityLookupData = this.citystateEventLocation.lookupModelDataForUpdate;
        this.setCoordinatesFromLookupData(targetRow, cityLookupData);
        
        // Force the auto home citystate component to refresh its display
        this.citystateCityAutoHome.displayComponentData(targetRow, null, 0);
    }

    /** This is an event handler for the afterModeChange event of sourceDriverEvents. */
    sourceDriverEventsAfterModeChange(event: DataSourceModeChangeEvent) {
        this.panelCreateAutoHome.visible = event.newMode === DataSourceMode.ADD;
    }

    /** This is an event handler for the onDisplay event of sourceAutoHomeLocation. */
    sourceAutoHomeLocationOnDisplay(event: DataDisplayEvent) {
        this.switchCreateAutoHome.visible = getDispatchControlBoolean("enable_auto_home")
    }

    public async inactivateAutoHomeLocationOnStatusChange(): Promise<boolean> {
        if (this.sourceDriverEvents.mode == DataSourceMode.UPDATE) {
            if (this.mainDataSource?.activeRow?.getChangedData().hasOwnProperty("event_status")) {
                const status = this.mainDataSource?.activeRow?.get("event_status");
                const autoHomeLocId = this.sourceAutoHomeLocation.activeRow?.get("id");
                const isAutoHomeActive = this.sourceAutoHomeLocation.activeRow?.get("is_active") === "Y";
                if (autoHomeLocId && isAutoHomeActive && (status === "C" || status === "V")) {
                    const yesNo = await CommonDialogs.showYesNo("Do you wish to inactivate the associated Auto Home Location for this event?", "Inactivate Auto Home Location");
                    if (yesNo) {
                        this.sourceAutoHomeLocation.activeRow.set("is_active", "N");
                        return true;
                    }
                    else if (yesNo == null) {
                        return false;
                    }
                }
            }
        }
        return true;
    }
}
